/**
 * A packet's plain-text scope as sections (#191): `THE WORK:` headings, real bullet lists, paragraphs. The stored text
 * stays plain. The server twin is `apps/api/app/domain/packet_text.py` (contractor email); keep the two grammars the
 * same.
 */
const HEADING = /^\s*([A-Z][A-Z /&']{2,60}):\s*(.*)$/
const BULLET = /^\s*(?:[-*•]|\d+[.)])\s+(.*\S)\s*$/

export type PacketBlock = { kind: 'heading' | 'paragraph'; text: string } | { kind: 'bullets'; items: string[] }

export function parsePacket(text: string | null | undefined): PacketBlock[] {
  const blocks: PacketBlock[] = []
  let para: string[] = []
  const flush = () => {
    if (para.length > 0) blocks.push({ kind: 'paragraph', text: para.join(' ') })
    para = []
  }
  for (const raw of (text ?? '').split(/\r?\n/)) {
    const line = raw.trim()
    if (!line) {
      flush()
      continue
    }
    const heading = HEADING.exec(line)
    if (heading?.[1] && heading[1].trim() === heading[1].trim().toUpperCase()) {
      flush()
      blocks.push({ kind: 'heading', text: heading[1].trim() })
      if (heading[2]?.trim()) para.push(heading[2].trim())
      continue
    }
    const bullet = BULLET.exec(line)
    if (bullet?.[1]) {
      flush()
      const last = blocks[blocks.length - 1]
      if (last?.kind === 'bullets') last.items.push(bullet[1])
      else blocks.push({ kind: 'bullets', items: [bullet[1]] })
      continue
    }
    para.push(line)
  }
  flush()
  return blocks
}

export function headingLabel(text: string): string {
  return text === text.toUpperCase() ? text.charAt(0) + text.slice(1).toLowerCase() : text
}

export function PacketText({ text, className, empty }: { text: string | null | undefined; className?: string; empty?: string }) {
  const blocks = parsePacket(text)
  if (blocks.length === 0) return empty ? <p className="text-muted-foreground">{empty}</p> : null
  return (
    <div className={className} data-slot="packet-text">
      {blocks.map((block, index) => {
        const key = `${block.kind}-${index}`
        if (block.kind === 'heading')
          return (
            <h4 key={key} className="mt-2 font-semibold first:mt-0">
              {headingLabel(block.text)}
            </h4>
          )
        if (block.kind === 'bullets')
          return (
            <ul key={key} className="mt-1 list-disc space-y-0.5 pl-5">
              {block.items.map((item, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: lines may repeat; order is the identity
                <li key={`${i}-${item}`}>{item}</li>
              ))}
            </ul>
          )
        return (
          <p key={key} className="mt-1">
            {block.text}
          </p>
        )
      })}
    </div>
  )
}
